import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
// Limitless imports
import { MFA_CTX_SETUP, MFA_CTX_LOGIN } from "@limitless/portal";
import { log as pilog } from "@limitless/common/utils";
import { useTranslation, useAppConfig } from "@limitless/common/hooks";
import MFAVerify from "./MFAVerify";
import { MFASetupContextProvider } from "../../hooks/mfa-setup/MFASetupContext";
import MFAPhoneSubmit from "./MFAPhoneSubmit";
import MfaComponentWrapper from "./MfaComponentWrapper";
import { useLoginWrapperContext } from "../../../expert";
import { getInitialMfaState } from "../../hooks/mfa-setup/MFAVerifyHook";
const useStyles = makeStyles(theme => ({
  fieldContainer: {
    width: "100%",
    maxWidth: 399,
    marginBottom: 11,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around"
  },
  errorMessage: {
    minHeight: 19,
    marginBottom: 9,
    color: theme.palette.error.main,
    alignSelf: "center"
  }
}));
const MFASetup = ({
  profile,
  verifySuccessCB,
  screen,
  mfaCtx
}) => {
  const log = pilog.getLogger("MFASetup");
  const {
    config: {
      dialCodes
    }
  } = useAppConfig();
  const {
    mfaLoginState
  } = useLoginWrapperContext();
  React.useEffect(() => {
    if ((dialCodes === null || dialCodes === void 0 ? void 0 : dialCodes.length) > 0) {
      log.debug("dialCodes: ", dialCodes);
    } else {
      log.debug("dialCodes not yet downloaded");
    }
  }, [dialCodes]);
  const classes = useStyles();
  const [mfaState, setMfaState] = React.useState(getInitialMfaState(dialCodes, profile, screen, mfaLoginState));
  const {
    t
  } = useTranslation();
  const wrapper = React.useMemo(() => {
    var _profile$_phoneDetail;
    let subtitle1 = t("Please enter your phone number. You'll receive a verification code via WhatsApp to verify your account. If we can't reach you on WhatsApp, we'll send your code by SMS instead.");
    if (!mfaLoginState.isPhoneNumberVerify && mfaLoginState.phoneExistInCognito || mfaLoginState.isMfaChallenged) {
      subtitle1 = t("We sent a verification code to {0}.", mfaState.phoneNumber);
    }
    let subtitle2;
    if (mfaCtx === MFA_CTX_LOGIN) {
      subtitle2 = t("The code is valid for 3 minutes.");
    } else if (!mfaLoginState.isPhoneNumberVerify && mfaLoginState.phoneExistInCognito && mfaCtx === MFA_CTX_SETUP) {
      subtitle2 = t("The code is valid for 24 hours.");
    }
    let subtitle3;
    if (!mfaLoginState.isPhoneNumberVerify && (mfaLoginState.phoneExistInCognito || profile !== null && profile !== void 0 && (_profile$_phoneDetail = profile._phoneDetails) !== null && _profile$_phoneDetail !== void 0 && _profile$_phoneDetail.phoneNumber) || mfaLoginState.isMfaChallenged) {
      subtitle3 = t("Please enter the code below:");
    }
    let header = t("Account Verification");
    if (mfaCtx === MFA_CTX_LOGIN) {
      header = t("Sign In");
    }
    return {
      header,
      subtitle1,
      subtitle2,
      subtitle3
    };
  }, [mfaState]);
  const setToPhoneScreen = React.useCallback(() => {
    setMfaState({
      ...mfaState,
      submitPhoneScreen: true
    });
  });
  return /*#__PURE__*/React.createElement(MFASetupContextProvider, null, /*#__PURE__*/React.createElement(MfaComponentWrapper, {
    header: wrapper.header,
    subtitle1: wrapper.subtitle1,
    subtitle2: wrapper.subtitle2,
    subtitle3: wrapper.subtitle3,
    userName: profile.userName,
    mfaCtx: mfaCtx
  }, mfaVerificationFrozen => {
    return /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement("div", {
      className: classes.fieldContainer
    }, !profile.primaryContactNumber && !(mfaLoginState !== null && mfaLoginState !== void 0 && mfaLoginState.isPhoneNumberVerify) && !mfaLoginState.isMfaChallenged ? /*#__PURE__*/React.createElement(MFAPhoneSubmit, {
      mfaState: mfaState,
      setMfaState: setMfaState,
      dialCodes: dialCodes
    }) : /*#__PURE__*/React.createElement(MFAVerify, {
      t: t,
      setToPhoneScreen: setToPhoneScreen,
      mfaType: mfaState.mfaType,
      successCB: verifySuccessCB,
      mfaVerificationFrozen: mfaVerificationFrozen,
      mfaState: mfaState
    })));
  }));
};
export default MFASetup;
MFASetup.propTypes = {
  verifySuccessCB: PropTypes.func,
  screen: PropTypes.string,
  mfaCtx: PropTypes.string,
  profile: PropTypes.object
};
MFASetup.defaultProps = {
  screen: "submitPhoneScreen"
};